"use client";

import React, { useState, useEffect, useCallback } from "react";
import CustomDialogue from "@shared/src/components/common/misc/CustomDialogue";
import { IoCheckmarkCircle, IoInformationCircle, IoWarning } from "react-icons/io5";
import { RiCloseLine } from "react-icons/ri";
import { Button, type ButtonProps } from "@shared/src/components/ui/button";
import { Spinner } from "@shared/src/components/common/misc/Spinner";
import { getErrorMessage } from "@shared/src/utils";
import { cn } from "@shared/src/utils/cn";

type ModalAlertType = "success" | "error" | "warning" | "info" | "loading" | "custom";
type ButtonVariant = ButtonProps["variant"];

export interface ModalAlertProps {
  // Alert specific props
  type: ModalAlertType;
  title?: string;
  messageTitle?: string;
  message?: string | React.ReactNode;
  description?: string;
  buttonText?: string;
  buttonClick?: () => Promise<any> | any;
  buttonVariant?: ButtonVariant;

  // Left button props
  leftButtonText?: string;
  leftButtonClick?: () => Promise<void>;
  leftButtonVariant?: ButtonVariant;
  showLeftButton?: boolean;

  // Behavior props
  autoClose?: boolean;
  autoCloseDelay?: number;
  preventClose?: boolean;

  // Allowed CustomDialogue props
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function ModalAlert({
  type,
  title,
  messageTitle,
  message,
  description,
  buttonText,
  buttonClick,
  buttonVariant = "primary",
  leftButtonText,
  leftButtonClick,
  leftButtonVariant = "surface-secondary",
  autoClose,
  autoCloseDelay = 1000,
  preventClose: userPreventClose,
  open: controlledOpen,
  onOpenChange,
}: ModalAlertProps) {
  const [loading, setLoading] = useState(false);
  const [leftButtonLoading, setLeftButtonLoading] = useState(false);
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);

  leftButtonText = leftButtonClick ? leftButtonText ?? "Cancel" : leftButtonText;
  buttonText = buttonClick ? buttonText ?? "OK" : buttonText;

  // Handle controlled/uncontrolled state
  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = useCallback(
    (newOpen: boolean) => {
      setUncontrolledOpen(newOpen);
      onOpenChange?.(newOpen);
    },
    [onOpenChange],
  );

  // Force preventClose when type is loading
  const preventClose = type === "loading" ? true : autoClose ? false : userPreventClose;

  // Handle all closing behavior here
  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      // If preventClose is true, only allow opening, not closing
      if (preventClose && !newOpen) {
        return;
      }
      setOpen(newOpen);
    },
    [preventClose, setOpen],
  );

  // Auto-close effect
  useEffect(() => {
    if (autoClose && !preventClose && open) {
      const timer = setTimeout(() => {
        handleOpenChange(false);
      }, autoCloseDelay);

      return () => clearTimeout(timer);
    }
  }, [autoClose, preventClose, open, autoCloseDelay, handleOpenChange]);

  const getIconWithBackground = () => {
    const size = 40;
    const iconProps = { size, className: `text-icons-icon-${type}` };
    const backgroundClass = `bg-surfaces-bg-${type} bg-opacity-20`;

    const icon = (() => {
      switch (type) {
        case "success":
          return <IoCheckmarkCircle {...iconProps} />;
        case "error":
          return <RiCloseLine {...iconProps} />;
        case "warning":
          return <IoWarning {...iconProps} />;
        case "info":
          return <IoInformationCircle {...iconProps} />;
        case "loading":
          return <Spinner size={size} className="text-icons-icon-primary" />;
        case "custom":
          return null;
      }
    })();

    return icon && <div className={cn("rounded-full p-4", backgroundClass)}>{icon}</div>;
  };

  const handleButtonClick = async () => {
    if (buttonClick) {
      try {
        setLoading(true);
        await buttonClick();
        setLoading(false);
        setOpen(false);
      } catch (error) {
        setLoading(false);
        console.error("Button click failed:", error);
      }
    }
  };

  const handleLeftButtonClick = async () => {
    if (leftButtonClick) {
      try {
        setLeftButtonLoading(true);
        await leftButtonClick();
        setLeftButtonLoading(false);
      } catch (error) {
        setLeftButtonLoading(false);
        console.error("Left button click failed:", error);
      }
    }
  };

  const icon = getIconWithBackground();

  return (
    <CustomDialogue
      open={open}
      onOpenChange={handleOpenChange}
      title={title}
      size="md"
      padding="md"
      background="primary"
      headerVariant="centered"
      preventClose={preventClose ? { enabled: true, hideCloseButton: true } : undefined}
      startTitle={true}
    >
      {closeModal => (
        <div className="flex flex-col items-center space-y-2">
          {/* Icon with background */}
          {icon && <div className="mb-4">{icon}</div>}

          {/* Content */}
          <div className="w-full space-y-2">
            {messageTitle && (
              <p className="text-text-primary mb-5 font-extrabold text-large text-center">{messageTitle}</p>
            )}
            {description && (
              <p className="text-text-secondary text-small text-center">
                {type === "error" ? getErrorMessage(description) : description}
              </p>
            )}
            {message && typeof message == "string" ? (
              <p className="text-text-secondary text-small text-center">
                {type === "error" ? getErrorMessage(message) : message}
              </p>
            ) : (
              message && <div className="text-center">{message}</div>
            )}
          </div>

          {/* Action Buttons */}
          {(buttonClick || leftButtonClick) && (
            <div className="flex items-center justify-center gap-4 w-full pt-4">
              {leftButtonClick && (
                <Button
                  variant={leftButtonVariant}
                  size="xl"
                  radius="full"
                  width="flex"
                  onClick={handleLeftButtonClick}
                  disabled={leftButtonLoading || loading}
                >
                  {leftButtonLoading ? <Spinner size={20} className="text-current" /> : leftButtonText}
                </Button>
              )}
              {buttonClick && (
                <Button
                  variant={buttonVariant}
                  size="xl"
                  radius="full"
                  width="flex"
                  onClick={handleButtonClick}
                  disabled={loading || leftButtonLoading}
                >
                  {loading ? <Spinner size={20} className="text-current" /> : buttonText}
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </CustomDialogue>
  );
}
